import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, ShieldCheck, Flame, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OwnerClaimBanner — shown on harvested (aggregate) property detail pages.
 *
 * Renders only when:
 *   - property.source === 'aggregate' (harvested, not owner-listed)
 *   - claim_status is not 'claimed' / 'claimed_by_agent'
 *
 * Loads /properties/aggregate/:ref/owner-claim-preview to pull live
 * warm-buyer count and trial length, then drives the inline signup form
 * that posts to /properties/aggregate/:ref/owner-claim — which auto-logs
 * the new owner in (30-day cookie + token).
 */
export const OwnerClaimBanner = ({ property, onClaimed }) => {
  const refId = property?.ref_number || property?.aggregate_id || property?.id;
  const isAggregate = property?.source === 'aggregate';
  const alreadyClaimed = ['claimed', 'claimed_by_agent'].includes(property?.claim_status);

  const [preview, setPreview] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '' });

  const loadPreview = useCallback(async () => {
    if (!isAggregate || !refId) return;
    try {
      const res = await axios.get(`${API}/properties/aggregate/${encodeURIComponent(refId)}/owner-claim-preview`);
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  }, [isAggregate, refId]);

  useEffect(() => {
    loadPreview();
    // Auto-expand if URL has ?claim=1 (deep-link from nudge emails/WA)
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('claim') === '1') setExpanded(true);
    } catch { /* ignore */ }
  }, [loadPreview]);

  if (!isAggregate || alreadyClaimed || !preview || preview.claimed) {
    return null;
  }

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.password) {
      toast.error('Email & password required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/properties/aggregate/${encodeURIComponent(refId)}/owner-claim`,
        form,
        { withCredentials: true },
      );
      // Store token so AuthContext picks it up on next refresh
      if (res.data?.token) {
        try { localStorage.setItem('token', res.data.token); } catch { /* ignore */ }
      }
      toast.success('Listing claimed — 14-day Verified Partner trial started');
      if (onClaimed) onClaimed(res.data);
      // Soft redirect to seller dashboard with success flag
      setTimeout(() => {
        window.location.href = '/seller?claim=success';
      }, 900);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not claim this listing — try a different email';
      toast.error(typeof msg === 'string' ? msg : 'Claim failed');
    } finally {
      setSubmitting(false);
    }
  };

  const warm = preview.warm_matches || 0;
  const trialDays = preview.trial_days || 14;

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-r from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-l-4 border-[#D4AF37] text-white mb-6"
      data-testid="owner-claim-banner"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#D4AF37] blur-3xl" />
      </div>

      {!expanded ? (
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 px-5 py-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="hidden sm:flex h-10 w-10 items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/40">
              <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <p className="text-xs tracking-[0.18em] uppercase text-[#D4AF37] font-bold mb-1">
                Are you the owner?
              </p>
              <p className="text-sm sm:text-base text-white/90 leading-snug">
                Claim this listing free in 60s and unlock buyer enquiries.
                {warm >= 2 && (
                  <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-[#E07A3A]/20 border border-[#E07A3A]/40 text-[#E07A3A] text-xs font-semibold">
                    <Flame className="h-3 w-3" /> {warm} warm buyer{warm === 1 ? '' : 's'} watching
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(true)}
            data-testid="owner-claim-banner-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0C] px-5 py-2.5 text-xs font-bold tracking-[0.15em] uppercase hover:bg-white transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Claim Free · {trialDays}-day trial
          </button>
        </div>
      ) : (
        <div className="relative px-5 py-5 sm:px-7 sm:py-6">
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-3 right-3 text-white/40 hover:text-white"
            aria-label="Close"
            data-testid="owner-claim-banner-close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs tracking-[0.18em] uppercase text-[#D4AF37] font-bold mb-2">
                Claim your listing
              </p>
              <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl mb-3 leading-tight">
                Verified Partner status — <span className="text-[#D4AF37]">14 days free</span>, no card
              </h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex gap-2"><span className="text-[#D4AF37]">✓</span>See real buyer enquiries on this listing</li>
                <li className="flex gap-2"><span className="text-[#D4AF37]">✓</span>Verified Partner badge — boosts trust & SEO</li>
                <li className="flex gap-2"><span className="text-[#D4AF37]">✓</span>Cancel anytime — £29/mo after trial only if you stay</li>
                {warm >= 2 && (
                  <li className="flex gap-2 text-[#E07A3A] font-semibold">
                    <Flame className="h-4 w-4" /> {warm} buyer{warm === 1 ? '' : 's'} actively shortlisting in {preview.city}
                  </li>
                )}
              </ul>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="owner-claim-form">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="owner-claim-name"
              />
              <input
                type="email"
                placeholder="Email *"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="owner-claim-email"
              />
              <input
                type="tel"
                placeholder="WhatsApp / phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="owner-claim-phone"
              />
              <input
                type="password"
                placeholder="Choose a password (6+ chars) *"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="owner-claim-password"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="owner-claim-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0C] px-4 py-3 text-xs font-bold tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Claiming…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Start free {trialDays}-day trial
                  </>
                )}
              </button>
              <p className="text-[10px] text-white/40 leading-relaxed text-center">
                By claiming you agree to Shopperden's Terms. Your contact stays hidden until a verified buyer is matched.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerClaimBanner;
